/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import React from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Formik, FormikHelpers } from "formik";
import { CardActions, CardContent, Typography, Tooltip, Grid } from "@mui/material";
import * as yup from "yup";
import { sortBy } from "lodash";
import { HistoryBackButton } from "components/buttons";
import { Button } from "components/haven-design/buttons";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { SwitchWithErrorLabel } from "components/haven-design/form/checkbox";
import { getFormDefinitions_formDefinitions as FormDraft, FormType, GetProperties, LoadForm, LoadFormVariables, getFormQuestions, getFormQuestions_formQuestions as FormQuestion } from "types/gql-types";
import { MaterialFormikSelect as SelectWithLabel } from "components/haven-design/form/select";
import { LoadingSpinner } from "components/graphql-views";
import { useSnackbar } from "components/haven-design/snackbar";
import { TOGGLE_FORM_OPEN, TOGGLE_FORM_DISPLAY } from "pages/dashboard/settings/property/lists/list-settings";
import { GET_FORM_QUESTIONS } from "../forms-questions";
import FormBuilder from "./form-builder";
import { FormBuilderStateProvider, useFormBuilderStateContext } from "./form-builder/form-builder-state-context";
import FormWithBuilderSubmit from "./form-builder/form-with-builder-submit";
import TOMLValidator, { validateContent } from "./toml-validator";
import { saveAndPublishFormDefinition, SubmitFormResult } from "./form-definition-actions";
import { findInvalidQuestionDataKeys } from "./form-builder/question-data-keys";
import { LOAD_FORM } from "./index";

export type FormDefinition = Omit<FormDraft, "__typename" | "id" | "property"> & {
  content: string, id: string | null | undefined
};

type Props = {
  initialData: FormDefinition;
  formType: "update" | "new";
  handleSubmit: (values: FormDefinition) => Promise<SubmitFormResult>;
}

const formType = Object.keys(FormType);

export const defaultFormSchema = yup.object().shape({
  __typename: yup.string().strip(true),

  id: yup.string().strip(true),

  propertyId: yup
    .string()
    .trim()
    .required(),

  name: yup
    .string()
    .trim()
    .required(),

  enabled: yup
    .boolean()
    .required(),

  createInterest: yup
    .boolean()
    .required(),

  skipSignature: yup.boolean().default(false),
  renderParagraphs: yup.boolean().default(false),

  ageRequirement: yup.number().nullable().required().default(18),
  ageRequirementOrDisabilityOverride: yup.boolean().nullable().default(false),

  content: yup
    .string()
    .trim()
    .required(),

  formType: yup.string().oneOf(formType).required(),

  formId: yup.string().nullable(),
  form: yup.object().shape({
    id: yup.string(),
    display: yup.boolean(),
    open: yup.boolean(),
  }).nullable(),
});

export const GET_PROPERTIES = gql`
  query GetProperties {
    properties {
      id
      name
    }
  }
`;

const publishTooltip = (values:FormDefinition):string => {
  if (!values.id) return "Form must be saved";
  if (!values.enabled) return "Form must be enabled";

  return "Publish form";
};

const TomlValidatorWithBuilderApply: React.FC<{
  editorMode: "builder" | "toml";
  content: string;
  existingQuestions: Record<string, string>;
}> = ({ editorMode, content, existingQuestions }) => {
  const { applyBuilderToContent } = useFormBuilderStateContext();
  return (
    <TOMLValidator
      content={content}
      existingQuestions={existingQuestions}
      resolveContentBeforeValidate={
        // eslint-disable-next-line no-undefined
        editorMode === "builder" ? async () => (await applyBuilderToContent()) ?? content : undefined
      }
    />
  );
};

const FormPublishWithBuilderApply: React.FC<{
  editorMode: "builder" | "toml";
  values: FormDefinition;
  existingQuestions: Record<string, string>;
  disabled: boolean;
  setFieldValue: FormikHelpers<FormDefinition>["setFieldValue"];
  submitForm: () => Promise<SubmitFormResult | void>;
  loadForm: (options: { variables: { id: string } }) => Promise<{ data?: LoadForm | null }>;
}> = ({ editorMode, values, existingQuestions, disabled, setFieldValue, submitForm, loadForm }) => {
  const { applyBuilderToContent } = useFormBuilderStateContext();
  const { openSnackbar } = useSnackbar();

  return (
    <Button
      type="button"
      size="L"
      color="primary"
      disabled={disabled}
      onClick={async () => {
        let effectiveContent = values.content;
        if (editorMode === "builder") {
          const built = await applyBuilderToContent();
          if (built === null) {
            openSnackbar({ message: "Fix builder errors before publishing.", status: "error" });
            return;
          }
          await setFieldValue("content", built);
          effectiveContent = built;
        }
        if (validateContent(effectiveContent, existingQuestions).length !== 0) {
          openSnackbar({ message: "Please check TOML validity", status: "error" });
          return;
        }

        await saveAndPublishFormDefinition({
          submitForm,
          loadForm,
          formDefinitionId: values.id ?? "",
          setFieldValue,
          openSnackbar,
        });
      }}
    >
      Save and Publish
    </Button>
  );
};

const DefinitionsForm: React.FC<Props> = (props) => {
  const { initialData, handleSubmit, formType: recordFormType } = props;
  const [editorMode, setEditorMode] = React.useState<"builder" | "toml">("builder");
  const [loadForm] = useMutation<LoadForm, LoadFormVariables>(LOAD_FORM);
  const { loading, error, data } = useQuery<GetProperties>(GET_PROPERTIES);
  const { loading: fQLoading, error: fQError, data: fQData } =
  useQuery<getFormQuestions>(GET_FORM_QUESTIONS, { fetchPolicy: "no-cache" });
  const [existingQuestions, setQuestions] = React.useState<Record<string, string>>({});
  const { openSnackbar } = useSnackbar();

  const [toggleFormOpen, { loading: formOpenLoading }] = useMutation(TOGGLE_FORM_OPEN);
  const [toggleFormDisplay, { loading: formDisplayLoading }] = useMutation(TOGGLE_FORM_DISPLAY);

  React.useEffect(() => {
    if (!fQLoading && !fQError && fQData?.formQuestions) {
      const questionsMap = (fQData.formQuestions as FormQuestion[])
        .reduce((acc, question) =>
          ({ ...acc, [question.name]: question?.valueType }), {});
      setQuestions(questionsMap);
    }
  }, [fQData, fQLoading]);

  if (loading) return <LoadingSpinner />;
  if (error || !data || !data?.properties) return <span>Server Error</span>;

  const properties = sortBy(data?.properties || [], [(property) => property.name]);

  return (
    <Formik
      initialValues={initialData}
      onSubmit={(values) => {
        // Blocks Save, not just Publish: a data key the backend cannot load as an
        // existing atom saves cleanly and then 500s every applicant on the live form.
        const [dataKeyError] = findInvalidQuestionDataKeys(values.content, existingQuestions);
        if (dataKeyError) {
          openSnackbar({ message: dataKeyError, status: "error" });
          return Promise.resolve({ saved: false });
        }
        return handleSubmit(values);
      }}
      validationSchema={defaultFormSchema}>
      {({ values, setFieldValue, submitForm }) => (
        <FormBuilderStateProvider
          content={values.content}
          existingQuestions={existingQuestions}
          onApplyContent={async (nextContent) => {
            await setFieldValue("content", nextContent);
          }}
        >
          <FormWithBuilderSubmit editorMode={editorMode} showSaveSuccess={recordFormType === "update"}>
            <CardContent>
              <Grid container spacing={1}>

                <Grid size={{ xs: 12, md: 6, lg: 5 }}>
                  <InputWithErrorLabel label="Name *" name="name" type="text" />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 5 }}>
                  <SelectWithLabel title="Property" name="propertyId"
                    options={[
                      { label: "Select", value: "" },
                      ...properties.map(({ id, name }) => ({ label: name, value: id })),
                    ]} />
                </Grid>

                <Grid size={{ xs: 12, md: 6, lg: 5 }}>
                  <SelectWithLabel title="Form type" name="formType"
                    options={[
                      { label: "Select", value: "" },
                      ...formType.map((formTypeId) => ({ label: formTypeId, value: formTypeId })),
                    ]} />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <SwitchWithErrorLabel label="Is form safe to be applied to?" name="enabled" />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <SwitchWithErrorLabel label="Allowed to create an interest?" name="createInterest" />
                </Grid>

                <Grid container size={{ xs: 12 }} spacing={1}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <SwitchWithErrorLabel
                      label="Disable Signature?"
                      name="skipSignature"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <SwitchWithErrorLabel
                      label="Render Paragraphs on the PDF?"
                      name="renderParagraphs"
                    />
                  </Grid>
                </Grid>

                <Grid container size={{ xs: 12 }} spacing={1}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <InputWithErrorLabel
                      label="Minimum Age Requirement *"
                      name="ageRequirement"
                      type="number"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <SwitchWithErrorLabel
                      label="Disability Overrides Minimum Age Requirement? *"
                      name="ageRequirementOrDisabilityOverride"
                    />
                  </Grid>
                </Grid>

              </Grid>

              <Grid container spacing={1}>
                <Grid size={{ xs: 12 }}>
                  <Grid container spacing={1}>
                    <Grid>
                      <Button
                        size="L"
                        type="button"
                        color={editorMode === "builder" ? "primary" : "secondary"}
                        onClick={() => setEditorMode("builder")}
                      >
                      Builder
                      </Button>
                    </Grid>
                    <Grid>
                      <Button
                        size="L"
                        type="button"
                        color={editorMode === "toml" ? "primary" : "secondary"}
                        onClick={() => setEditorMode("toml")}
                      >
                      Raw TOML
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  {editorMode === "builder" && (
                    <FormBuilder />
                  )}
                  {editorMode === "toml" && (
                    <InputWithErrorLabel label="Content *" name="content" type="text" multiline maxRows={18} />
                  )}
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              <Grid container spacing={1}>
                <Grid>
                  <HistoryBackButton size="L" />
                </Grid>
                <Grid>
                  <Button color="primary" size="L" type="submit">
                  Save
                  </Button>
                </Grid>
                <Grid>
                  <TomlValidatorWithBuilderApply
                    editorMode={editorMode}
                    content={values.content}
                    existingQuestions={existingQuestions}
                  />
                </Grid>
                <Grid>
                  <Tooltip title={publishTooltip(values)}>
                    <div>
                      <FormPublishWithBuilderApply
                        editorMode={editorMode}
                        values={values}
                        existingQuestions={existingQuestions}
                        disabled={!values.id || !values.enabled}
                        setFieldValue={setFieldValue}
                        submitForm={submitForm}
                        loadForm={loadForm}
                      />
                    </div>
                  </Tooltip>
                </Grid>
              </Grid>
            </CardActions>
            <CardContent>
              <Grid container spacing={1}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="h6">Form Settings</Typography>
                </Grid>

                <Grid>
                  {(recordFormType === "update") ? (
                    <SwitchWithErrorLabel
                      label="Form is open?"
                      name="form.open"
                      disabled={!values.form}
                      checked={values.form?.open}
                      onChange={() => {
                        if (!formOpenLoading) {
                          toggleFormOpen({ variables: { id: values.formId } })
                            .finally(() => {
                              openSnackbar({ message: "Form Open status updated", status: "success" });
                              setFieldValue("form.open", !values.form?.open);
                            });
                        }
                      }}
                    />
                  ) : (
                    <SwitchWithErrorLabel
                      label="Form is open?"
                      name="form.open"
                      disabled={!values.form}
                    />
                  )}
                </Grid>
                {(recordFormType === "update") && (
                  <Grid>
                    <SwitchWithErrorLabel
                      label="Form is showcased?"
                      name="form.display"
                      disabled={!values.form}
                      checked={values.form?.display}
                      onChange={() => {
                        if (!formDisplayLoading) {
                          toggleFormDisplay({ variables: { id: values.formId } })
                            .finally(() => {
                              openSnackbar({ message: "Form display status updated", status: "success" });
                              setFieldValue("form.display", !values.form?.display);
                            });
                        }
                      }}
                    />
                  </Grid>)}
                {values.formId && (
                  <Grid>
                    {`${location.origin}/form/${values.formId}`}
                  </Grid>)}
              </Grid>
            </CardContent>
          </FormWithBuilderSubmit>
        </FormBuilderStateProvider>
      )}
    </Formik>
  );
};

export default DefinitionsForm;
